import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseDialog } from 'src/library/components/BaseDialog';
import './d-form';
import '../fields/d-view-info';
import type { FormDialogInput } from 'src/library/components/form-dialog-input';
import type { FormDialogResult } from 'src/library/components/form-dialog-result';
import type { FormElement } from 'src/library/components/form';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import { t } from 'src/i18n';

@customElement('form-dialog')
export class FormDialog extends BaseDialog<FormDialogInput, FormDialogResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      .title {
        flex-grow: 1;
        background: url(/images/forms-color.svg) -4px -2px no-repeat;
        background-size: 44px 44px;
        padding-top: 4px;
        padding-left: 44px;
      }
      d-view-info {
        border: 1px solid var(--borderColor);
        border-bottom: none;
        background: hsl(0, 0%, 96%);
        padding: 12px 12px 12px 4px;
        line-height: 150%;
      }
      @container dialog (width > 700px) {
        .title {
          background-position: -6px 0;
          background-size: 60px 60px;
          padding-top: 14px;
          padding-left: 60px;
        }
      }
    `,
  ];
  @property({ type: Number, attribute: 'width' })
  width = 784;
  @property({ type: String })
  title: string = '';
  @property({ type: Array })
  content: FormElement[] = [];
  @property({ type: Array })
  userNames: SelectDropdownOption[] = [];
  @property({ type: String })
  currentUserUuid: string = '';
  @property({ type: String })
  prompt: string = '';
  @property({ type: String })
  mode: 'editAsRespondent' | 'editAsResponsible' = 'editAsRespondent';
  @property({ type: Boolean })
  isSurvey = false;
  @property({ type: Boolean })
  anonymous = false;

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'close' },
      { name: t('forms.formDialogDone'), action: 'save', main: true },
    ];
  }

  private get infoContent() {
    if (this.anonymous) {
      return `<ul class="info-list"><li>${this.prompt}</li></ul>
      <ul class="info-list"><li>${t('forms.formDialogAnonymousInfo')}</li></ul>`;
    }
    return `<ul class="info-list"><li>${this.prompt}</li></ul>
      <ul class="alert-list"><li>${t('forms.formDialogSensitiveWarning')}</li></ul>`;
  }

  protected fetchResult(detail: string | undefined): FormDialogResult {
    if (detail === 'save') {
      return {
        action: 'done',
        content: this.content,
      };
    }
    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: FormDialogInput) {
    this.title = input.title;
    this.prompt = input.prompt;
    this.content = input.content;
    this.isSurvey = input.isSurvey;
    this.anonymous = input.anonymous;
    this.userNames = input.userNames;
    this.currentUserUuid = input.currentUserUuid;
    this.mode = input.mode;
  }

  protected renderBody() {
    return html`
      ${this.mode === 'editAsRespondent'
        ? html` <div class="form-info">
            <d-view-info .content=${this.infoContent} class="fullWidth"></d-view-info>
          </div>`
        : nothing}
      <d-form
        .mode=${this.mode}
        .content=${this.content}
        .isSurvey=${this.isSurvey}
        .anonymous=${this.anonymous}
        .userNames=${this.userNames}
        .currentUserUuid=${this.currentUserUuid}
        @item-changed=${this.onFormChanged}
      ></d-form>
    `;
  }

  private onFormChanged(e: CustomEvent<{ value: FormElement[] }>) {
    e.stopPropagation();
    this.content = e.detail.value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'form-dialog': FormDialog;
  }
}
